import {type BlobRange, parseFileAnchor} from '@github-ui/blob-anchor'
import {useMemo, useState, useSyncExternalStore} from 'react'

function subscribeToHashChange(callback: () => void) {
  window.addEventListener('hashchange', callback)
  window.addEventListener('statechange', callback)
  return () => {
    window.removeEventListener('hashchange', callback)
    window.removeEventListener('statechange', callback)
  }
}

function getWindowHashSnapshot(): string {
  return window.location.hash
}

function getServerHashSnapshot(): string {
  return ''
}

function parseHighlightedLines(hash: string): BlobRange | undefined {
  const anchInfo = parseFileAnchor(hash)
  return anchInfo.blobRange?.start?.line ? anchInfo.blobRange : undefined
}

/**
 * Derives the highlighted line range (e.g. `#L10-L20`) from the URL hash.
 *
 * The hash is updated through two independent channels, and any single change travels through only
 * one of them:
 *
 *  - **Router navigations** (selecting a symbol in the symbols panel, browser back/forward) update
 *    the router location — surfaced here as `routerHash` from `useLocation()` — but fire no
 *    `hashchange`/`statechange` event. Under TanStack Router `window.location` is not updated until
 *    the navigation commits, *after* `routerHash` already reflects the target, and for same-route
 *    hash navigations no further render is scheduled once it settles.
 *  - **Imperative updates** (keyboard cursor navigation assigns `window.location.hash`; clicking a
 *    line number calls `updateUrlHash`) mutate `window.location` and fire `hashchange`/`statechange`,
 *    but never go through the router, so `routerHash` stays stale.
 *
 * Watching only `window.location.hash` misses router navigations until some unrelated later render
 * happens to re-read it — which is why selecting a symbol used to highlight its line only on the
 * *next* navigation. Watching only `routerHash` misses the imperative updates. We watch both and
 * adopt whichever value changed.
 */
export function useHashHighlightedLines(routerHash: string): BlobRange | undefined {
  const windowHash = useSyncExternalStore(subscribeToHashChange, getWindowHashSnapshot, getServerHashSnapshot)

  const [activeHash, setActiveHash] = useState(windowHash)
  const [prevRouterHash, setPrevRouterHash] = useState(routerHash)
  const [prevWindowHash, setPrevWindowHash] = useState(windowHash)

  if (routerHash !== prevRouterHash) {
    setPrevRouterHash(routerHash)
    setActiveHash(routerHash)
  }
  if (windowHash !== prevWindowHash) {
    setPrevWindowHash(windowHash)
    setActiveHash(windowHash)
  }

  return useMemo(() => parseHighlightedLines(activeHash), [activeHash])
}
